import { FinanceRate } from '@kanda-libs/ks-frontend-services';
import { useCallback, useMemo } from 'react';
import { formatToPercentage } from 'utils';
import { useFinanceCalculatorContext } from '../../context';

export interface RateClassNames {
  container: string;
  wrapper: string;
  rate: string;
  separator: string;
}

export interface RateHook {
  aprLabel: string;
  maxBorrow: number;
  termLabel: string;
  classNames: RateClassNames;
  selected: boolean;
  onClick: () => void;
}

export default function useRate(
  apr: string,
  rates: FinanceRate[],
  isLoading: boolean,
): RateHook {
  const { stored, onSelectAPR } = useFinanceCalculatorContext();

  const selectedApr = stored?.apr;

  const aprLabel = useMemo(() => {
    if (isLoading) return '--.--';
    const isBnpl = apr.includes('-bnpl');
    return `${formatToPercentage(parseInt(apr, 10))} ${
      isBnpl ? 'BNPL' : 'APR'
    }`;
  }, [isLoading, apr]);

  const maxBorrow = useMemo(() => {
    if (isLoading) return 2500000;
    return Math.max(...rates.map((rate) => rate?.max_job_value || 0));
  }, [isLoading, rates]);

  const termLabel = useMemo(() => {
    if (isLoading) return '0 years';
    if (rates.length === 1) {
      const { duration } = rates[0];
      if (!duration) return '--';
      if (duration < 12) return `${duration} months`;
      if (duration % 12 !== 0) return `${duration} months`;
      if (duration === 1) return '1 year';
      return `${duration / 12} years`;
    }
    const [d1, d2] = [
      Math.min(...rates.map((rate) => rate?.duration || 0)),
      Math.max(...rates.map((rate) => rate?.duration || 0)),
    ];
    if (d2 < 12) return `${d1}-${d2} months`;
    if (d1 % 12 !== 0 || d2 % 12 !== 0) return `${d1}-${d2} months`;
    return `${d1 / 12}-${d2 / 12} years`;
  }, [isLoading, rates]);

  const selected = useMemo(() => selectedApr === apr, [selectedApr, apr]);

  const classNames = useMemo(
    () => ({
      container: [
        'flex border rounded p-4 gap-x-4 text-left shadow-card',
        selected
          ? 'border-purple-300 bg-purple-50'
          : 'border-neutral-300 bg-neutral-000 hover:bg-purple-50',
      ].join(' '),
      wrapper:
        'grid grid-cols-[auto_18px_auto_auto] md:grid-cols-[120px_18px_auto_auto] w-full h-full',
      rate: [
        'text-14-22-em my-auto mx-auto',
        selected ? 'text-purple-400' : 'text-neutral-600',
      ].join(' '),
      separator: [
        'border-r-2 h-full mx-auto',
        selected ? 'border-purple-300' : 'border-neutral-300',
      ].join(' '),
    }),
    [selected],
  );

  const onClick = useCallback(() => {
    if (selected) return;
    onSelectAPR(apr);
  }, [apr, selected, onSelectAPR]);

  return {
    aprLabel,
    maxBorrow,
    termLabel,
    classNames,
    selected,
    onClick,
  };
}
